import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { 
  Bell, 
  Check, 
  CheckCheck,
  X,
  Clock,
  CheckSquare,
  FileText,
  Target,
  Activity,
  AlertTriangle,
  BookOpen,
  Trash2,
  ExternalLink,
  RefreshCw,
  Rocket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { format, formatDistanceToNow } from 'date-fns';
import NotificationDetailDialog from './NotificationDetailDialog';

const NOTIFICATION_ICONS = {
  task_pending_approval: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  task_tentative: { icon: CheckSquare, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  task_rejected: { icon: X, color: 'text-red-400', bg: 'bg-red-500/10' },
  task_approved: { icon: Check, color: 'text-green-400', bg: 'bg-green-500/10' },
  artifact_pending_approval: { icon: FileText, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  artifact_approved: { icon: FileText, color: 'text-green-400', bg: 'bg-green-500/10' },
  artifact_rejected: { icon: FileText, color: 'text-red-400', bg: 'bg-red-500/10' },
  mission_status_change: { icon: Target, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  objective_activation_requested: { icon: Target, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  objective_activated: { icon: Target, color: 'text-green-400', bg: 'bg-green-500/10' },
  activity_status_change: { icon: Activity, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  issue_created: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  issue_assigned: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  progress_report_submitted: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  principles_validation_needed: { icon: BookOpen, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  training_request_status: { icon: BookOpen, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  app_update: { icon: Rocket, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  feedback_status_change: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10' }
};

function getDeepLink(notification) {
  const { source_type, source_id, deep_link_params, type, metadata } = notification;
  
  switch (source_type) {
    case 'daily_task':
      // Link to daily tasks page with date, user filter, and open modal
      const taskParams = new URLSearchParams();
      if (deep_link_params?.date) taskParams.set('date', deep_link_params.date);
      if (deep_link_params?.user_email) taskParams.set('user', deep_link_params.user_email);
      if (deep_link_params?.task_id) {
        taskParams.set('taskId', deep_link_params.task_id);
        taskParams.set('openModal', 'taskDetails');
      }
      return `dailytasks?${taskParams.toString()}`;
    
    case 'artifact':
      return `artifacts?artifact=${source_id}`;
    
    case 'mission':
      return `missions?mission=${source_id}`;
    
    case 'objective':
      return `discovery?objective=${source_id}`;
    
    case 'activity':
      return `activities?activity=${source_id}`;
    
    case 'issue':
      return `issues?issue=${source_id}`;
    
    case 'progress_report':
      return `activities?activity=${deep_link_params?.activity_id}&report=${source_id}`;
    
    case 'training_request':
      return `profile?tab=training`;
    
    case 'feedback':
      return source_id ? `Dashboard?feedbackId=${source_id}` : '';
    
    default:
      return '';
  }
}

export default function NotificationCenter() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });
  
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      return base44.entities.Notification.filter(
        { recipient_email: user.email },
        '-created_date',
        50
      );
    },
    enabled: !!user?.email,
    refetchInterval: 30000 // Refetch every 30 seconds
  });
  
  const markReadMutation = useMutation({
    mutationFn: async (notificationId) => {
      await base44.entities.Notification.update(notificationId, {
        read: true,
        read_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
  
  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const unread = notifications.filter(n => !n.read);
      await Promise.all(unread.map(n => 
        base44.entities.Notification.update(n.id, {
          read: true,
          read_at: new Date().toISOString()
        })
      ));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: async (notificationId) => {
      await base44.entities.Notification.delete(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markReadMutation.mutate(notification.id);
    }
    setOpen(false);
  };

  const handleNotificationDetail = (notification) => {
    if (!notification.read) {
      markReadMutation.mutate(notification.id);
    }
    setSelectedNotification(notification);
    setDetailOpen(true);
  };
  
  return (
    <><Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative text-slate-400 hover:text-slate-200 hover:bg-slate-800"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 bg-slate-900 border-slate-700" align="end">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="font-semibold text-slate-100">Notifications</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => markAllReadMutation.mutate()}
              className="text-xs text-slate-400 hover:text-slate-200"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[400px]">
          {isLoading ? (
            <div className="p-4 text-center text-slate-400">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {(() => {
                // Deduplicate notifications by source_id + type, keeping the latest
                const seen = new Map();
                const deduped = [];
                for (const n of notifications) {
                  const key = `${n.source_id}_${n.type}`;
                  if (!seen.has(key)) {
                    seen.set(key, true);
                    deduped.push(n);
                  }
                }
                return deduped;
              })().map(notification => {
                const iconConfig = NOTIFICATION_ICONS[notification.type] || { 
                  icon: Bell, 
                  color: 'text-slate-400', 
                  bg: 'bg-slate-500/10' 
                };
                const Icon = iconConfig.icon;
                const deepLink = getDeepLink(notification);
                
                return (
                  <div 
                    key={notification.id}
                    className={`p-4 hover:bg-slate-800/50 transition-colors cursor-pointer ${
                      !notification.read ? 'bg-slate-800/30' : ''
                    }`}
                    onClick={() => handleNotificationDetail(notification)}
                  >
                    <div className="flex gap-3">
                      <div className={`p-2 rounded-lg ${iconConfig.bg} shrink-0`}>
                        <Icon className={`w-4 h-4 ${iconConfig.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${
                            !notification.read ? 'text-slate-100' : 'text-slate-300'
                          }`}>
                            {notification.title}
                          </p>
                          <div className="flex items-center gap-1 shrink-0">
                            {!notification.read && (
                              <span className="w-2 h-2 bg-cyan-400 rounded-full" />
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-6 h-6 text-slate-500 hover:text-red-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteMutation.mutate(notification.id);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-slate-400 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        {notification.metadata?.rejection_reason && (
                          <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-300">
                            <span className="font-medium">Reason: </span>
                            {notification.metadata.rejection_reason}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-500">
                            {formatDistanceToNow(new Date(notification.created_date), { addSuffix: true })}
                          </span>
                          {notification.action_url === 'refresh' ? (
                            <button
                              onClick={() => {
                                handleNotificationClick(notification);
                                window.location.reload();
                              }}
                              className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 font-medium"
                            >
                              <RefreshCw className="w-3 h-3" />
                              Tap to refresh
                            </button>
                          ) : deepLink ? (
                            <Link 
                              to={createPageUrl(deepLink)}
                              onClick={() => handleNotificationClick(notification)}
                              className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                            >
                              View <ExternalLink className="w-3 h-3" />
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        

      </PopoverContent>
    </Popover>

    <NotificationDetailDialog
      notification={selectedNotification}
      open={detailOpen}
      onOpenChange={setDetailOpen}
      deepLink={selectedNotification ? getDeepLink(selectedNotification) : ''}
    />
    </>
  );
}